// ═══════════════════════════════════════════════════════════════
// FeedbackModal — fifteen seconds to tell you something.
//
// The beta's bargain is that people report what's wrong and ask for
// what's missing. That only happens if it's this easy; anything that
// requires opening email gets thought about and then not done.
//
// Deliberately does NOT ask for a build number, a browser, or a
// screen name — all of that is attached automatically. A mechanic
// should be able to type one sentence and get back to the car.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { X, Send, Check, MessageSquarePlus } from 'lucide-react';
import { submitFeedback, FEEDBACK_KINDS } from './lib/use-feedback.js';

export default function FeedbackModal({ onClose, initialKind = 'feature' }) {
  const [kind, setKind] = useState(initialKind);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await submitFeedback({ kind, title, detail });
      setSent(true);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div
        className="modal-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-box fb-sent" role="dialog" aria-modal="true">
          <div className="fb-sent-mark"><Check size={30} strokeWidth={3} /></div>
          <h2>Got it — thank you</h2>
          <p className="muted">
            This goes straight to Brad. During the beta, everything that gets sent is read.
          </p>
          <div className="form-actions" style={{ justifyContent: 'center' }}>
            <button type="button" className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-box" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 style={{ flex: 1, margin: 0 }}>
            <MessageSquarePlus size={17} style={{ verticalAlign: '-3px', marginRight: 6 }} />
            Tell us something
          </h2>
          <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form className="form" onSubmit={handleSubmit}>
          <fieldset className="fb-kinds">
            <legend className="fb-legend">What kind of thing?</legend>
            {FEEDBACK_KINDS.map((k) => (
              <label key={k.value} className={`fb-kind${kind === k.value ? ' active' : ''}`}>
                <input
                  type="radio"
                  name="kind"
                  value={k.value}
                  checked={kind === k.value}
                  onChange={() => setKind(k.value)}
                />
                <span>
                  <strong>{k.label}</strong>
                  <span className="muted small">{k.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="form-row">
            <span>In one line</span>
            <input
              value={title}
              maxLength={140}
              required
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Let me text an estimate to two numbers"
            />
          </div>

          <div className="form-row">
            <span>Any detail (optional)</span>
            <textarea
              rows={4}
              maxLength={4000}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="What you were doing, what you expected, what happened instead."
            />
          </div>

          <p className="muted small fb-context-note">
            Your build, the screen you're on, and your device are attached automatically —
            no need to describe them.
          </p>

          {error && <p className="form-error">{error}</p>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn" disabled={submitting}>
              <Send size={15} strokeWidth={2.5} />
              {submitting ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
